import { createFileRoute, redirect } from "@tanstack/react-router";
import { SignInPage } from "../../features/auth";
import { sanitizeRedirectPath } from "../../features/auth/utils/redirect";
import { getI18n, isValidLocale, switchLanguage } from "../../lib/i18n";

/**
 * The sign-in page in the guest's language (/fr/sign-in). The page is the
 * same as /sign-in; the language comes from the first path segment, which the
 * root reads for SSR, and the browser loads its strings before rendering.
 */
export const Route = createFileRoute("/$lang/sign-in")({
  beforeLoad: async ({ params, location }) => {
    if (!isValidLocale(params.lang)) {
      // Not a language (/foo/sign-in): the plain sign-in page, query kept.
      throw redirect({ href: `/sign-in${location.searchStr}`, statusCode: 301 });
    }
    if (typeof window !== "undefined") await switchLanguage(params.lang);
  },
  component: SignInPage,
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => {
    const redirect = sanitizeRedirectPath(search.redirect);
    return redirect ? { redirect } : {};
  },
  head: ({ params }) => {
    const t = getI18n(isValidLocale(params.lang) ? params.lang : "en").t;
    const title = t("auth.meta.sign_in_title");
    const description = t("auth.meta.sign_in_description");
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: "noindex,nofollow" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
    };
  },
});
